/**
 * DeckPreview — a stored deck, shown the way the agent that made it showed it.
 *
 * A deck an agent produces is not a bag of shapes. It is rendered from a
 * doc_model through the tenant's design skill, and that design — the fonts, the
 * colours, the logo, the layouts — is what the artifact panel puts on screen
 * the moment the file is made. Filing it in the Library used to lose all of it:
 * the same deck reopened as plain slide sheets, so the file looked like a
 * poorer object than the one the agent had just shown. The design was never
 * gone, only unasked for.
 *
 * So this asks. The server answers from the FILE's own record — its frozen
 * doc_model and brand stamp — and `null` is the ordinary answer, not a failure:
 * a deck someone uploaded has no design of ours, and painting a stranger's file
 * in this tenant's brand would misrepresent what the file is. Then the slide
 * sheets render instead, which show what the file actually contains.
 *
 * Tri-state, so the design never arrives as a second frame after the sheets
 * have already been read: undefined = still asking (spinner), string = the
 * design, null = there isn't one (sheets).
 */

import React, { useEffect, useState } from 'react';
import { Spin } from 'antd';
import api from '@/services/api';
import { useLanguage } from '@/hooks/useLanguage';
import { SlideDeckView, slidesFromDeckEnvelope } from './SlideDeckView';
import './SlideDeckView.css';

interface Props {
  /** The Library file this deck is stored as. */
  fileId: string;
  /** The deck envelope (shapes and paragraphs), used when there is no design. */
  envelope: string;
  title?: string;
}

export const DeckPreview: React.FC<Props> = ({ fileId, envelope, title }) => {
  const { t } = useLanguage();
  const [html, setHtml] = useState<string | null | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    setHtml(undefined);
    api.get(`/vault/files/${fileId}/deck-design`)
      .then((res) => { if (!cancelled) setHtml((res.data?.html as string) || null); })
      .catch(() => { if (!cancelled) setHtml(null); });
    return () => { cancelled = true; };
  }, [fileId]);

  if (html === undefined) {
    return (
      <div className="deck-preview-loading">
        <Spin />
      </div>
    );
  }
  if (typeof html === 'string') {
    return (
      <iframe
        className="artifact-html-frame"
        title={title || t('vault.viewer.deckFrameTitle')}
        sandbox="allow-same-origin"
        srcDoc={html}
      />
    );
  }
  return <SlideDeckView slides={slidesFromDeckEnvelope(safeParse(envelope))} />;
};

const safeParse = (value: string): unknown => {
  try {
    return JSON.parse(value || '{}');
  } catch {
    return {};
  }
};

export default DeckPreview;
